<template>
  <div :id="carouselId" class="swiper--container">
    <Swiper
      :style="cssVars"
      v-bind="$attrs"
      :modules="[EffectFade, Navigation, Pagination, Autoplay, FreeMode]"
      :navigation="{
        enabled: true,
        nextEl: `#${carouselId} .nuxt-ui-button-next`,
        prevEl: `#${carouselId} .nuxt-ui-button-prev`,
      }"
      :cssMode="$device.isMobile"
      :grab-cursor="true"
      @progress="onProgressSwiper"
    >
      <SwiperSlide
        v-for="(item, index) in items"
        :key="index"
        :index="index"
        :class="{ '!w-auto': ($attrs['free-mode'] as any)?.enabled }"
        class="!h-auto"
      >
        <slot :name="`${'default' as string}`" :item></slot>
      </SwiperSlide>
    </Swiper>
    <template v-if="showNavigation">
      <UButton
        class="nuxt-ui-button-prev"
        :color="mergedPrevButton.color"
        :class="mergedPrevButton.class"
      >
        <UIcon class="text-lg" name="i-fluent:chevron-left-24-filled" />
      </UButton>
      <UButton
        class="nuxt-ui-button-next"
        :color="mergedNextButton.color"
        :class="mergedNextButton.class"
      >
        <UIcon name="i-fluent:chevron-right-24-filled" />
      </UButton>
    </template>
  </div>
  <div
    v-if="displayGradient && showGradient"
    class="bg-gradient-to-l from-white to-transparent w-24 h-full absolute right-0 bottom-0 z-[1]"
  />
</template>

<script lang="ts" setup generic="T extends Record<string, any> | string">
import type { ButtonColor } from '#ui/types'
import type { Swiper as SwiperType } from 'swiper'
import {
  Autoplay,
  EffectFade,
  FreeMode,
  Navigation,
  Pagination,
} from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { twMerge } from 'tailwind-merge'

import 'swiper/css'
import 'swiper/css/effect-cards'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/free-mode'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    displayGradient?: boolean
    items?: T[]
    nextButton?: CarouselButton
    prevButton?: CarouselButton
    showNavigation?: boolean
  }>(),
  {
    displayGradient: false,
    items: () => [],
    nextButton: () => ({
      class: '',
    }),
    prevButton: () => ({
      class: '',
    }),
    showNavigation: true,
  },
)

const defaultButtonClasses
  = 'h-8 w-8 p-1 flex items-center justify-center absolute top-1/2 transform -translate-y-1/2 rounded-full z-[2] disabled:!opacity-0 '

const carouselId = ref('')
const showGradient = ref(true)

type CarouselButton = {
  class?: string
  color?: ButtonColor
  icon?: string
}

const mergedPrevButton = computed<CarouselButton>(() => ({
  color: 'white',
  icon: 'i-fluent:chevron-left-24-filled',
  ...props.prevButton,
  class: twMerge(
    `${defaultButtonClasses} lg:-left-4 sm:left-4 left-2`,
    props.prevButton.class,
  ),
}))

const mergedNextButton = computed<CarouselButton>(() => ({
  color: 'white',
  icon: 'i-fluent:chevron-right-24-filled',
  ...props.nextButton,
  class: twMerge(
    `${defaultButtonClasses} lg:-right-4 sm:right-4 right-2`,
    props.nextButton.class,
  ),
}))

const cssVars = computed(() => ({
  '--swiper-navigation-sides-offset': '20px',
  '--swiper-navigation-size': '20px',
  '--swiper-theme-color': '#fff',
}))

function onProgressSwiper(swiper: SwiperType) {
  showGradient.value = !swiper.isEnd
}

onBeforeMount(
  () =>
    (carouselId.value = `carousel-${Math.random().toString(36).substring(3)}`),
)
</script>
